import { SmartLinkDecisionAction } from '../dtos/enums/smart-link-decision-action';
import { SmartLinkEntityType } from '../dtos/enums/smart-link-entity-type';
import {
  REJECT_REASONS,
  SmartLinkRejectReason,
} from '../dtos/enums/smart-link-reject-reason';

export type SmartLinkEvidenceField = 'headline' | 'standfirst' | 'body';

export interface ISmartLinkEvidence {
  field: SmartLinkEvidenceField;
  matched_text: string;
}

// Consumed raw (snake_case): the contract is frozen, so there is no camelCase mapping layer.
export interface ISmartLink {
  smart_link_id: string;
  target_db: string;
  record_id: string;
  entity_type: SmartLinkEntityType;
  display_name: string;
  confidence: number;
  rank: number;
  // Shared by two or more items to mark them mutually exclusive readings of one article
  // mention. An opaque key, compared by value — never parsed.
  ambiguity_group: string | null;
  evidence: ISmartLinkEvidence[];
  pipeline_version_id: string;
  decision_action: SmartLinkDecisionAction | null;
  decision_reason: SmartLinkRejectReason | null;
}

export type SmartLinkTier = 'Strong' | 'Possible' | 'Weak';

// Bands drive colour, grouping, sorting and bulk-accept — not how much of the bar is filled,
// which is the score itself. Weak is also the cutoff for the "weaker matches" toggle.
export function confidenceTier(confidence: number): SmartLinkTier {
  if (confidence >= 0.75) {
    return 'Strong';
  }
  if (confidence >= 0.5) {
    return 'Possible';
  }
  return 'Weak';
}

// Takes the pair, not just the type, so a future datasource can label its chips differently
// (e.g. a CW fund vs a Private Markets fund) without a signature change.
export function entityTypeLabel(
  item: Pick<ISmartLink, 'target_db' | 'entity_type'>
): string {
  if (item.entity_type === SmartLinkEntityType.FUND) {
    return 'Fund';
  }
  if (item.entity_type === SmartLinkEntityType.MANAGEMENT_GROUP) {
    return 'Group';
  }
  if (item.entity_type === SmartLinkEntityType.MANAGER) {
    return 'Manager';
  }
  if (item.entity_type === SmartLinkEntityType.SECTOR) {
    return 'Sector';
  }
  if (item.entity_type === SmartLinkEntityType.ASSET_CLASS) {
    return 'Asset class';
  }
  if (item.entity_type === SmartLinkEntityType.INVESTMENT_TRUST) {
    return 'Investment trust';
  }
  return item.entity_type;
}

const EVIDENCE_FIELD_LABELS: Record<string, string> = {
  headline: 'Headline',
  standfirst: 'Standfirst',
  body: 'Body',
};

export function evidenceFieldLabel(field: SmartLinkEvidenceField): string {
  return EVIDENCE_FIELD_LABELS[field] ?? field;
}

// Falls back to the wire value so a reason added to the contract ahead of the UI still renders.
export function rejectReasonLabel(reason: SmartLinkRejectReason): string {
  return (
    REJECT_REASONS.find((candidate) => candidate.value === reason)?.label ??
    reason
  );
}

// allItems is the full run result, decided items included, so a card's hint does not change
// when a sibling is accepted or rejected.
export function ambiguityAlternativeNames(
  item: ISmartLink,
  allItems: ISmartLink[]
): string[] {
  if (item.ambiguity_group === null) {
    return [];
  }
  return allItems
    .filter(
      (candidate) =>
        candidate.ambiguity_group === item.ambiguity_group &&
        candidate.smart_link_id !== item.smart_link_id
    )
    .map((candidate) => candidate.display_name);
}
